import IRunnerCard from '@Shared.Angular/@types/core/contracts/queryModel/card/runnerCard';
import IStepNode from '@Shared.Angular/@types/core/contracts/queryModel/modeler/stepNode';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import IModelerNode from '@Shared.Angular/@types/modelerNode';
import { IModelerNodeLink } from '@Shared.Angular/@types/workflow';
import {
  ApprovalTaskOptions,
  FormFieldType,
  NodeCategory
} from '@Shared.Angular/flowingly.services/flowingly.constants';

export function detectLoops(
  modelerNodes: IModelerNode[],
  nodes: IStepNode[],
  modelerNodesByKey: Map<number, IModelerNode>,
  linksByFromKey: Map<number, IModelerNodeLink[]>
): { fromNodeKey: number; message: string }[] {
  const loopNodeKeysByBackLink = new Map<IModelerNodeLink, Set<number>>();
  const startNode = modelerNodes.find((n) => n.isInitial === 'true');
  if (!startNode) {
    return [];
  }

  const futurePaths = [] as {
    startLink: IModelerNodeLink;
    visitedNodeKeys: Set<number>;
  }[];
  let firstStepKey: number;
  for (const link of linksByFromKey.get(startNode.key) || []) {
    firstStepKey = link.to;
    futurePaths.push({
      startLink: link,
      visitedNodeKeys: new Set()
    });
  }

  while (futurePaths.length > 0) {
    const currentPath = futurePaths.pop();
    let pendingLink = currentPath.startLink;

    while (pendingLink) {
      const currentLink = pendingLink;
      pendingLink = null;
      const toNode = modelerNodesByKey.get(currentLink.to);
      if (!toNode) {
        continue;
      }
      if (currentPath.visitedNodeKeys.has(toNode.key)) {
        if (!loopNodeKeysByBackLink.has(currentLink)) {
          loopNodeKeysByBackLink.set(
            currentLink,
            findAllNodeKeysOnAllPathsBetween(
              currentLink.to,
              currentLink.from,
              modelerNodesByKey,
              linksByFromKey
            )
          );
        }
        continue;
      }
      currentPath.visitedNodeKeys.add(toNode.key);
      const nextLinks = linksByFromKey.get(toNode.key) || [];
      if (nextLinks.length > 0) {
        pendingLink = nextLinks[0];
        for (let i = 1; i < nextLinks.length; i++) {
          futurePaths.push({
            startLink: nextLinks[i],
            visitedNodeKeys: new Set(currentPath.visitedNodeKeys)
          });
        }
      }
    }
  }

  return getInfiniteLoopErrors(
    loopNodeKeysByBackLink,
    modelerNodesByKey,
    linksByFromKey,
    nodes,
    firstStepKey
  );
}

function getInfiniteLoopErrors(
  loopNodeKeysByBackLink: Map<IModelerNodeLink, Set<number>>,
  modelerNodesByKey: Map<number, IModelerNode>,
  linksByFromKey: Map<number, IModelerNodeLink[]>,
  nodes: IStepNode[],
  firstStepKey: number
) {
  // A loop is ok if any of the nodes in the loop is:
  //  A. An Approval
  //  B. A Decision whose Step is part of the loop
  // which has a path leading to a Node not in the loop
  const errors = [] as { fromNodeKey: number; message: string }[];

  loopNodeKeysByBackLink.forEach((loopNodeKeys, link) => {
    const fromNode = modelerNodesByKey.get(link.from);
    const toNode = modelerNodesByKey.get(link.to);
    if (!fromNode || !toNode) {
      return;
    }

    for (const nodeKey of loopNodeKeys) {
      const loopNode = modelerNodesByKey.get(nodeKey);
      if (!loopNode) {
        continue;
      }
      const isApproval =
        loopNode.taskType === ActivityTaskType.APPROVAL ||
        loopNode.taskType === ActivityTaskType.PARALLEL_APPROVAL ||
        loopNode.taskType === ActivityTaskType.SEQUENTIAL_APPROVAL;
      if (!isApproval && loopNode.category !== NodeCategory.EXCLUSIVE_GATEWAY) {
        continue;
      }
      const linksFromNode = linksByFromKey.get(loopNode.key) || [];
      let hasLinkToNodeOutsideLoop = linksFromNode.some(
        (linkFromNode) => !loopNodeKeys.has(linkFromNode.to)
      );
      if (
        !hasLinkToNodeOutsideLoop &&
        isApproval &&
        !loopNodeKeys.has(firstStepKey)
      ) {
        const approvalNode = nodes.find((n) => n.modelerNodeId === loopNode.id);
        const approvalField = (
          approvalNode?.card as IRunnerCard
        )?.formElements?.find((fe) => fe.type === FormFieldType.APPROVAL_RULE);
        const isRejectToStepOneEnabled = approvalField?.options?.some(
          (option) =>
            option.value ===
              ApprovalTaskOptions.REJECT_AND_GO_BACK_TO_FIRST_TASK &&
            option.enabled
        );
        hasLinkToNodeOutsideLoop = isRejectToStepOneEnabled === true;
      }
      if (!hasLinkToNodeOutsideLoop) {
        continue;
      }
      if (isApproval) {
        return;
      }
      if (
        loopNode.category === NodeCategory.EXCLUSIVE_GATEWAY &&
        loopNodeKeys.has(loopNode.selectedNodeKey)
      ) {
        return;
      }
    }
    const message = `Backlink from '${fromNode.text}' to '${toNode.text}' creates an infinite loop and is invalid.`;
    errors.push({ fromNodeKey: fromNode.key, message });
  });
  return errors;
}

function findAllNodeKeysOnAllPathsBetween(
  startNodeKey: number,
  targetNodeKey: number,
  nodesByKey: Map<number, IModelerNode>,
  linksByFromKey: Map<number, IModelerNodeLink[]>
): Set<number> {
  const nodeKeysOnPaths = new Set<number>();
  const pathsToVisit: { nodeKey: number; visitedNodeKeys: number[] }[] = [
    { nodeKey: startNodeKey, visitedNodeKeys: [startNodeKey] }
  ];

  while (pathsToVisit.length > 0) {
    const currentPath = pathsToVisit.pop();

    if (currentPath.nodeKey === targetNodeKey) {
      currentPath.visitedNodeKeys.forEach((nodeKey) =>
        nodeKeysOnPaths.add(nodeKey)
      );
      continue;
    }

    const nextLinks = linksByFromKey.get(currentPath.nodeKey) || [];
    nextLinks.forEach((link) => {
      if (currentPath.visitedNodeKeys.includes(link.to)) {
        return;
      }

      if (!nodesByKey.has(link.to)) {
        return;
      }

      pathsToVisit.push({
        nodeKey: link.to,
        visitedNodeKeys: [...currentPath.visitedNodeKeys, link.to]
      });
    });
  }

  return nodeKeysOnPaths;
}
